import {
  Container,
  CssBaseline,
  NoSsr,
  Paper,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { green, lightBlue, lightGreen } from "@mui/material/colors";
import { useFormik } from "formik";
import { FunctionalComponent, h } from "preact";
import { lazy, Suspense, useEffect, useMemo, useState } from "preact/compat";
import {
  ComputedValues,
  computeValues,
  formatComputedValues,
} from "../computation";
import {
  getInitialValues,
  SolarDiverterForm,
  SolarDiverterFormSchema,
} from "../form";
import { theme } from "../theme";
import { ComputedValue } from "./computed-value";
import { CoverTypeSelect } from "./cover-type-select";
import { LookedUpField } from "./lookedup-field";

const Privacy = lazy(() => import("./privacy").then((mod) => mod.Privacy));

const App: FunctionalComponent = () => {
  const initialValues = useMemo<SolarDiverterForm>(getInitialValues, []);
  const form = useFormik<SolarDiverterForm>({
    initialValues,
    validationSchema: SolarDiverterFormSchema,
    validateOnBlur: true,
    onSubmit: () => console.log("submitted!"),
  });

  const [computedValues, setComputedValues] =
    useState<ComputedValues<string> | null>(null);

  useEffect(() => {
    if (form.isValid) {
      const valuesComputed = computeValues(form.values);
      setComputedValues(formatComputedValues(valuesComputed));
    }
  }, [form.values, form.isValid]);

  return (
    <div id="app">
      <ThemeProvider theme={theme}>
        <Container maxWidth="sm">
          <CssBaseline />
          <Typography variant="h2" component="h1" gutterBottom align="center">
            Solar Diverter Calculator
          </Typography>
          <Typography variant="subtitle1" gutterBottom align="center">
            Version 1.01
          </Typography>
          <Typography variant="subtitle2" gutterBottom align="center">
            Although best efforts have been taken to ensure accuracy, this
            utility may contain errors. Therefore, no warranty is provided.
          </Typography>
          <form onSubmit={form.handleSubmit}>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Water Requirement
              </Typography>
              <LookedUpField fieldId="familyMembers" form={form} />
              <LookedUpField fieldId="hotWaterReq" form={form} />
              <ComputedValue
                label="Total hot water requirement per year"
                tightBottom
              >
                {computedValues?.totalHotWaterReq} litres
              </ComputedValue>
            </Paper>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Energy Requirement
              </Typography>
              <LookedUpField fieldId="minCWTemp" form={form} />
              <LookedUpField fieldId="maxCWTemp" form={form} />
              <ComputedValue label="Average cold water temperature">
                {computedValues?.avgCWTemp}°C
              </ComputedValue>
              <LookedUpField fieldId="reqHWTemp" form={form} />
              <ComputedValue label="Average temperature difference between hot and cold">
                {computedValues?.heatDelta}°C
              </ComputedValue>
              <ComputedValue label="Energy required to heat one litre by that amount">
                {computedValues?.heatEnergyPerL} kWh
              </ComputedValue>
              <ComputedValue
                label="Energy required to heat a year's worth of water"
                tightBottom
              >
                {computedValues?.heatEnergyPerYear} kWh
              </ComputedValue>
            </Paper>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Gas Requirement
              </Typography>
              <LookedUpField fieldId="boilerEfficiency" form={form} />
              <ComputedValue label="Gas required to heat a year's worth of water">
                {computedValues?.gasReqPerYear} kWh
              </ComputedValue>
              <LookedUpField fieldId="gasCost" form={form} />
              <ComputedValue
                label="Total cost per year not using a solar diverter"
                color="secondary"
                tightBottom
              >
                {computedValues?.totalCostNoDiverter}
              </ComputedValue>
            </Paper>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Electricity Requirement
              </Typography>
              <LookedUpField fieldId="solarDiverterCost" form={form} />
              <LookedUpField fieldId="installationCost" form={form} />
              <ComputedValue label="Total investment made">
                {computedValues?.totalInvestment}
              </ComputedValue>
              <LookedUpField fieldId="daysInsufficientSolar" form={form} />
              <ComputedValue label="Days in year with sufficient solar generation">
                {computedValues?.daysSufficientSolar} days
              </ComputedValue>
              <ComputedValue
                label="Cost of gas per day to cover those days"
                color={lightBlue.A100}
              >
                {computedValues?.gasPerDayCover}
              </ComputedValue>
              <ComputedValue
                label="Cost of gas per year to cover those days"
                color={lightBlue.A100}
              >
                {computedValues?.gasPerYearCover}
              </ComputedValue>
              <LookedUpField fieldId="electricityCost" form={form} />
              <ComputedValue
                label="Cost of electricity per day to cover those days"
                color={lightGreen.A100}
              >
                {computedValues?.electricityPerDayCover}
              </ComputedValue>
              <ComputedValue
                label="Cost of electricity per year to cover those days"
                color={lightGreen.A100}
              >
                {computedValues?.electricityPerYearCover}
              </ComputedValue>
              <CoverTypeSelect form={form} />
              <ComputedValue
                label="Solar generation used to heat water each day"
                color={lightGreen.A100}
              >
                {computedValues?.electricityRequiredPerDay} kWh
              </ComputedValue>
              <ComputedValue
                label="Total electricity not exported as a result"
                color={lightGreen.A100}
              >
                {computedValues?.electricityNotExported} kWh
              </ComputedValue>
              <LookedUpField fieldId="electricityExportPrice" form={form} />
              <ComputedValue label="Lost export revenue">
                {computedValues?.lostExportRevenue}
              </ComputedValue>
              <ComputedValue
                label="Total cost per year using a solar diverter"
                tightBottom
              >
                {computedValues?.totalDiverterCost}
              </ComputedValue>
            </Paper>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                color={green.A700}
              >
                Payback Estimation
              </Typography>
              <Typography variant="overline" color={green.A700}>
                Number of years until payback of investment
              </Typography>
              <Typography variant="h4" sx={{ p: 1, mb: 2 }} color={green.A700}>
                {computedValues?.paybackPeriod}
              </Typography>
              <Typography variant="overline" color={green.A700}>
                Annual savings thereafter
              </Typography>
              <Typography variant="h4" sx={{ p: 1 }} color={green.A700}>
                {computedValues?.annualSavingsThereafter}
              </Typography>
            </Paper>
          </form>
          <Typography variant="subtitle2" align="center" sx={{ p: 2 }}>
            © 2022 Gary Does Solar |{" "}
            <Suspense fallback="Privacy policy">
              <NoSsr>
                <Privacy />
              </NoSsr>
            </Suspense>
          </Typography>
        </Container>
      </ThemeProvider>
    </div>
  );
};

export default App;
